import type { ScopeSectionSchema } from './types'

export const kybSections: ScopeSectionSchema[] = [
  {
    id: 'kyb_core',
    tabId: 'kyb',
    formTitle: 'KYB Configuration',
    formDescription: 'Know Your Business verification settings',
    pdfTitle: 'KYB Configuration',
    pdfDescription: 'Know Your Business verification settings',
    highLevelSummary: 'KYB enablement and required checks.',
    groups: [
      {
        id: 'kyb_tab_header',
        tabId: 'kyb',
        sectionId: 'kyb_core',
        formTitle: 'KYB Configuration',
        formDescription: 'Know Your Business verification settings',
        pdfTitle: 'KYB Configuration',
        pdfDescription: 'Know Your Business verification settings',
      },
    ],
    fields: [],
  },
  {
    id: 'kyb_enablement',
    tabId: 'kyb',
    formTitle: 'Enable KYB',
    formDescription: 'Enable business identity verification and compliance checks',
    pdfTitle: 'Enable KYB',
    pdfDescription: 'Whether KYB is enabled.',
    highLevelSummary: 'KYB enablement.',
    groups: [
      {
        id: 'kyb_enablement_group',
        tabId: 'kyb',
        sectionId: 'kyb_enablement',
        formTitle: 'Enable KYB Verification',
        helperText: 'Business identity verification and compliance checks',
        pdfTitle: 'Enable KYB',
        pdfDescription: 'Whether KYB is enabled.',
      },
    ],
    fields: [
      {
        id: 'kyb_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_enablement',
        groupId: 'kyb_enablement_group',
        path: 'kybConfig.enabled',
        valueType: 'boolean',
        formLabel: 'Enable KYB Verification',
        formDescription: 'Business identity verification and compliance checks',
        tooltip: 'Know Your Business (KYB) verification confirms business identity and legitimacy through registry checks, ownership verification, and compliance screening. KYB is required when onboarding business entities, companies, partnerships, trusts, or other organizations to meet regulatory obligations and prevent business identity fraud.',
        pdfLabel: 'KYB enabled',
        pdfDescription: 'Whether KYB is enabled.',
        contributesToCompletion: true,
      },
    ],
  },
  // Australian Business Checks
  {
    id: 'kyb_au_checks',
    tabId: 'kyb',
    formTitle: 'Australian Business Checks',
    formDescription: 'Australian business checks to run during onboarding.',
    pdfTitle: 'KYB - AU checks',
    pdfDescription: 'Australian KYB checks.',
    highLevelSummary: 'Australian business checks selected.',
    groups: [
      {
        id: 'kyb_au_checks_group',
        tabId: 'kyb',
        sectionId: 'kyb_au_checks',
        formTitle: 'Australian Business Checks',
        helperText: 'Select the checks required for Australian business verification.',
        pdfTitle: 'Australian Business Checks',
        pdfDescription: 'Australian business checks.',
      },
    ],
    fields: [
      {
        id: 'kyb_au_checks_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_au_checks',
        groupId: 'kyb_au_checks_group',
        path: 'kybConfig.auChecksEnabled',
        valueType: 'tri_state',
        formLabel: 'AU Checks Enabled',
        formDescription: 'Whether Australian business checks are required.',
        tooltip: 'Enable this to configure which Australian business verification checks should be performed during onboarding.',
        pdfLabel: 'AU checks enabled',
        pdfDescription: 'Whether AU business checks are enabled.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_au_check_aml_standard',
        tabId: 'kyb',
        sectionId: 'kyb_au_checks',
        groupId: 'kyb_au_checks_group',
        path: 'kybConfig.auChecks.amlStandard',
        valueType: 'boolean',
        formLabel: 'AML Standard (PEPs, Sanctions)',
        tooltip: 'Screens the business and its associated individuals against Politically Exposed Persons (PEP) databases and sanctions lists including OFAC, UN, EU, and DFAT. Required for AML/CTF compliance.',
        pdfLabel: 'AML standard',
        pdfDescription: 'AML standard checks for businesses.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_au_check_aml_enhanced',
        tabId: 'kyb',
        sectionId: 'kyb_au_checks',
        groupId: 'kyb_au_checks_group',
        path: 'kybConfig.auChecks.amlEnhanced',
        valueType: 'boolean',
        formLabel: 'AML Enhanced (Standard + Adverse Media)',
        tooltip: 'Includes all standard AML checks (PEPs and sanctions) plus adverse media screening for negative news coverage. Recommended for enhanced due diligence and high-risk business relationships.',
        pdfLabel: 'AML enhanced',
        pdfDescription: 'AML enhanced checks for businesses.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_au_check_asic_info',
        tabId: 'kyb',
        sectionId: 'kyb_au_checks',
        groupId: 'kyb_au_checks_group',
        path: 'kybConfig.auChecks.asicInfo',
        valueType: 'boolean',
        formLabel: 'ASIC Information (Current)',
        tooltip: 'Retrieves current company information from the Australian Securities and Investments Commission (ASIC) registry including company structure, directors, shareholders, registered office, and status.',
        pdfLabel: 'ASIC information',
        pdfDescription: 'ASIC current information checks.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_au_check_ubo',
        tabId: 'kyb',
        sectionId: 'kyb_au_checks',
        groupId: 'kyb_au_checks_group',
        path: 'kybConfig.auChecks.ubo',
        valueType: 'boolean',
        formLabel: 'UBO (Ultimate Beneficial Ownership)',
        tooltip: 'Identifies Ultimate Beneficial Owners - the natural persons who ultimately own or control the business entity. Traces ownership structures through corporate layers, trusts, and nominee arrangements.',
        pdfLabel: 'UBO',
        pdfDescription: 'Ultimate beneficial ownership checks.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_au_check_credit_report',
        tabId: 'kyb',
        sectionId: 'kyb_au_checks',
        groupId: 'kyb_au_checks_group',
        path: 'kybConfig.auChecks.creditReport',
        valueType: 'boolean',
        formLabel: 'Business Credit Report (CreditorWatch)',
        tooltip: 'Provides a comprehensive business credit report from CreditorWatch, covering creditworthiness, financial health, payment history, credit enquiries, court judgments, and defaults.',
        pdfLabel: 'Business credit report',
        pdfDescription: 'Business credit report check.',
        contributesToCompletion: false,
      },
    ],
  },
  // KYC for Individuals
  {
    id: 'kyb_kyc_individuals',
    tabId: 'kyb',
    formTitle: 'KYC for Individuals',
    formDescription: 'Whether KYC verification is required for individuals identified through KYB.',
    pdfTitle: 'KYB - KYC for Individuals',
    pdfDescription: 'KYC requirements for individuals in KYB.',
    highLevelSummary: 'KYC for individuals identified via KYB.',
    groups: [
      {
        id: 'kyb_kyc_individuals_group',
        tabId: 'kyb',
        sectionId: 'kyb_kyc_individuals',
        formTitle: 'KYC for Individuals',
        pdfTitle: 'KYC for Individuals',
        pdfDescription: 'KYC requirements for individuals.',
      },
    ],
    fields: [
      {
        id: 'kyb_kyc_individuals_required',
        tabId: 'kyb',
        sectionId: 'kyb_kyc_individuals',
        groupId: 'kyb_kyc_individuals_group',
        path: 'kybConfig.kycForIndividualsRequired',
        valueType: 'tri_state',
        formLabel: 'KYC for Individuals Required',
        formDescription: 'Whether KYC verification needs to be performed on individuals identified through KYB (directors, shareholders, beneficial owners).',
        tooltip: 'When KYB identifies individuals such as directors, shareholders, or beneficial owners, KYC verification can be run on them to verify their identity. This typically involves identity document checks, biometrics, and AML screening.',
        pdfLabel: 'KYC for individuals required',
        pdfDescription: 'Whether KYC is required for individuals.',
        contributesToCompletion: true,
      },
    ],
  },
  // UBO Consent & Workflow
  {
    id: 'kyb_ubo_consent',
    tabId: 'kyb',
    formTitle: 'UBO Consent & Workflow',
    formDescription: 'Consent requirements and workflow selection for UBO verification.',
    pdfTitle: 'KYB - UBO Consent & Workflow',
    pdfDescription: 'UBO consent awareness and workflow selection.',
    highLevelSummary: 'UBO consent and workflow configuration.',
    groups: [
      {
        id: 'kyb_ubo_consent_group',
        tabId: 'kyb',
        sectionId: 'kyb_ubo_consent',
        formTitle: 'UBO Consent & Workflow',
        pdfTitle: 'UBO Consent & Workflow',
        pdfDescription: 'UBO consent and workflow configuration.',
      },
    ],
    fields: [
      {
        id: 'kyb_ubo_consent_aware',
        tabId: 'kyb',
        sectionId: 'kyb_ubo_consent',
        groupId: 'kyb_ubo_consent_group',
        path: 'kybConfig.uboConsentAware',
        valueType: 'tri_state',
        formLabel: 'Client aware consent is required for UBO KYC',
        formDescription: 'Whether the client understands that running KYC on UBOs requires explicit consent from the individual.',
        tooltip: 'Running KYC verification on UBOs identified via KYB requires the individual\'s explicit consent before identity checks can proceed. The client needs to understand this requirement for their onboarding process design.',
        pdfLabel: 'UBO consent awareness',
        pdfDescription: 'Whether client is aware of UBO consent requirements.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_ubo_sms_phone_aware',
        tabId: 'kyb',
        sectionId: 'kyb_ubo_consent',
        groupId: 'kyb_ubo_consent_group',
        path: 'kybConfig.uboSmsPhoneAware',
        valueType: 'tri_state',
        formLabel: 'Client aware SMS delivery requires phone number',
        formDescription: 'Whether the client understands that sending the hosted URL via SMS requires the entity\'s phone number.',
        tooltip: 'If the client wants to send the hosted verification URL to UBOs via SMS, they must have each entity\'s phone number available in their system. This is a data requirement for the SMS delivery channel.',
        pdfLabel: 'SMS phone awareness',
        pdfDescription: 'Whether client is aware SMS requires phone number.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_ubo_workflow',
        tabId: 'kyb',
        sectionId: 'kyb_ubo_consent',
        groupId: 'kyb_ubo_consent_group',
        path: 'kybConfig.uboWorkflowId',
        valueType: 'select',
        formLabel: 'UBO Workflow',
        formDescription: 'Which workflow should be run on UBOs identified via KYB.',
        tooltip: 'Select the workflow from the Workflows tab that should be used to verify UBOs. This determines what checks (KYC, IDV, AML, etc.) will be performed on each identified UBO.',
        pdfLabel: 'UBO workflow',
        pdfDescription: 'Workflow to run on UBOs.',
        contributesToCompletion: false,
      },
    ],
  },
  // AML Business Entity Screening
  {
    id: 'kyb_aml_entity_screening',
    tabId: 'kyb',
    formTitle: 'AML Business Entity Screening',
    formDescription: 'AML screening for the business entity itself.',
    pdfTitle: 'KYB - AML Entity Screening',
    pdfDescription: 'AML screening for business entities.',
    highLevelSummary: 'AML screening for business entities.',
    groups: [
      {
        id: 'kyb_aml_entity_screening_group',
        tabId: 'kyb',
        sectionId: 'kyb_aml_entity_screening',
        formTitle: 'AML Business Entity Screening',
        pdfTitle: 'AML Business Entity Screening',
        pdfDescription: 'AML screening configuration for business entities.',
      },
    ],
    fields: [
      {
        id: 'kyb_aml_entity_screening_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_aml_entity_screening',
        groupId: 'kyb_aml_entity_screening_group',
        path: 'kybConfig.amlEntityScreening',
        valueType: 'tri_state',
        formLabel: 'AML Screen Business Entity',
        formDescription: 'Whether to AML screen the business entity.',
        tooltip: 'Screens the business entity itself against sanctions lists and adverse media databases. This is separate from screening individual officers or UBOs.',
        pdfLabel: 'AML entity screening',
        pdfDescription: 'Whether business entity is AML screened.',
        contributesToCompletion: true,
      },
      {
        id: 'kyb_aml_entity_screening_types',
        tabId: 'kyb',
        sectionId: 'kyb_aml_entity_screening',
        groupId: 'kyb_aml_entity_screening_group',
        path: 'kybConfig.amlEntityScreeningTypes',
        valueType: 'multi_select',
        formLabel: 'AML Screening Types',
        formDescription: 'Types of AML screening to apply to the business entity.',
        tooltip: 'Sanctions screening checks against global sanctions lists (OFAC, UN, EU, DFAT). Adverse Media screening searches for negative news coverage about the business.',
        pdfLabel: 'AML screening types',
        pdfDescription: 'AML screening types for business entity.',
        contributesToCompletion: false,
        options: [
          { value: 'sanctions', label: 'Sanctions' },
          { value: 'adverse_media', label: 'Adverse Media' },
        ],
        visibleWhen: [{ dependsOn: 'kybConfig.amlEntityScreening', equals: 'yes' }],
      },
    ],
  },
  // KYB AML Ongoing Monitoring
  {
    id: 'kyb_aml_ongoing_monitoring',
    tabId: 'kyb',
    formTitle: 'KYB AML Ongoing Monitoring',
    formDescription: 'Ongoing AML monitoring for business entities — provider-dependent (Refinitiv or CreditorWatch).',
    pdfTitle: 'KYB - AML Ongoing Monitoring',
    pdfDescription: 'KYB AML ongoing monitoring configuration.',
    highLevelSummary: 'KYB AML ongoing monitoring enablement and provider details.',
    groups: [
      {
        id: 'kyb_aml_ongoing_monitoring_group',
        tabId: 'kyb',
        sectionId: 'kyb_aml_ongoing_monitoring',
        formTitle: 'KYB AML Ongoing Monitoring',
        pdfTitle: 'KYB AML Ongoing Monitoring',
        pdfDescription: 'KYB AML ongoing monitoring configuration.',
      },
    ],
    fields: [
      {
        id: 'kyb_aml_ongoing_monitoring_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_aml_ongoing_monitoring',
        groupId: 'kyb_aml_ongoing_monitoring_group',
        path: 'kybConfig.kybAmlOngoingMonitoringEnabled',
        valueType: 'tri_state',
        formLabel: 'Enable KYB AML Ongoing Monitoring',
        formDescription: 'Whether ongoing AML monitoring is required for business entities.',
        tooltip: 'Ongoing AML monitoring continuously screens business entities against AML datasets. The coverage depends on the AML provider: Refinitiv provides PEP/sanctions/adverse-media monitoring, while CreditorWatch provides AU company/credit/registry monitoring.',
        pdfLabel: 'KYB AML ongoing monitoring',
        pdfDescription: 'Whether KYB AML ongoing monitoring is enabled.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_aml_ongoing_monitoring_notes',
        tabId: 'kyb',
        sectionId: 'kyb_aml_ongoing_monitoring',
        groupId: 'kyb_aml_ongoing_monitoring_group',
        path: 'kybConfig.kybAmlOngoingMonitoringNotes',
        valueType: 'textarea',
        formLabel: 'KYB AML Monitoring Notes',
        formDescription: 'Additional notes about KYB AML ongoing monitoring requirements.',
        placeholder: 'e.g., Specific monitoring requirements, notification preferences...',
        pdfLabel: 'KYB AML monitoring notes',
        pdfDescription: 'Additional KYB AML ongoing monitoring notes.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'kybConfig.kybAmlOngoingMonitoringEnabled', equals: 'yes' }],
      },
    ],
  },
  // Entity Types — Percentage Split
  {
    id: 'kyb_entity_types',
    tabId: 'advanced',
    formTitle: 'Entity Types',
    formDescription: 'Percentage split of business entity types.',
    pdfTitle: 'KYB - Entity types',
    pdfDescription: 'Entity type percentage split.',
    highLevelSummary: 'Entity type percentage breakdown.',
    groups: [
      {
        id: 'kyb_entity_types_group',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        formTitle: 'Entity Types',
        helperText: 'Enter the approximate percentage for each business entity type.',
        pdfTitle: 'Entity types',
        pdfDescription: 'Entity type percentage split.',
      },
    ],
    fields: [
      {
        id: 'kyb_entity_types_enabled',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.entityTypesEnabled',
        valueType: 'tri_state',
        formLabel: 'Entity Type Split Known',
        formDescription: 'Whether the client knows the percentage split of business entity types.',
        tooltip: 'Understanding the mix of entity types (sole traders, companies, trusts, etc.) helps scope the implementation and plan for different verification requirements per entity type.',
        pdfLabel: 'Entity type split known',
        pdfDescription: 'Whether entity type split is known.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_entity_split_sole_traders',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.entityTypeSplit.soleTradersPercent',
        valueType: 'number',
        formLabel: 'Sole Traders %',
        formDescription: 'Percentage of sole trader entities.',
        pdfLabel: 'Sole Traders %',
        pdfDescription: 'Percentage of sole trader entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_entity_split_pty_ltd',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.entityTypeSplit.ptyLtdPercent',
        valueType: 'number',
        formLabel: 'PTY LTD %',
        formDescription: 'Percentage of PTY LTD entities.',
        pdfLabel: 'PTY LTD %',
        pdfDescription: 'Percentage of PTY LTD entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_entity_split_company',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.entityTypeSplit.companyPercent',
        valueType: 'number',
        formLabel: 'Company %',
        formDescription: 'Percentage of company entities.',
        pdfLabel: 'Company %',
        pdfDescription: 'Percentage of company entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_entity_split_partnership',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.entityTypeSplit.partnershipPercent',
        valueType: 'number',
        formLabel: 'Partnership %',
        formDescription: 'Percentage of partnership entities.',
        pdfLabel: 'Partnership %',
        pdfDescription: 'Percentage of partnership entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_entity_split_trust',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.entityTypeSplit.trustPercent',
        valueType: 'number',
        formLabel: 'Trust %',
        formDescription: 'Percentage of trust entities.',
        pdfLabel: 'Trust %',
        pdfDescription: 'Percentage of trust entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_entity_split_other',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.entityTypeSplit.otherPercent',
        valueType: 'number',
        formLabel: 'Other %',
        formDescription: 'Percentage of other entity types.',
        pdfLabel: 'Other %',
        pdfDescription: 'Percentage of other entity types.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_other_entity_type',
        tabId: 'advanced',
        sectionId: 'kyb_entity_types',
        groupId: 'kyb_entity_types_group',
        path: 'kybConfig.otherEntityType',
        valueType: 'text',
        formLabel: 'Other Entity Type',
        formDescription: 'Describe other entity type.',
        placeholder: 'Describe other entity type',
        tooltip: 'Specify any additional entity types not listed in the standard options.',
        pdfLabel: 'Other entity type',
        pdfDescription: 'Other entity type description.',
        contributesToCompletion: false,
      },
    ],
  },
  // International KYB
  {
    id: 'kyb_international',
    tabId: 'kyb',
    formTitle: 'International KYB',
    formDescription: 'International KYB requirements by country with product selection.',
    pdfTitle: 'KYB - International',
    pdfDescription: 'International KYB requirements.',
    highLevelSummary: 'International KYB countries and products.',
    groups: [
      {
        id: 'kyb_international_group',
        tabId: 'kyb',
        sectionId: 'kyb_international',
        formTitle: 'International KYB',
        pdfTitle: 'International KYB',
        pdfDescription: 'International KYB requirements.',
      },
    ],
    fields: [
      {
        id: 'kyb_international_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_international',
        groupId: 'kyb_international_group',
        path: 'kybConfig.internationalEnabled',
        valueType: 'tri_state',
        formLabel: 'International KYB Required',
        formDescription: 'Whether international KYB verification is required.',
        tooltip: 'Enable this if you need to verify business entities registered in countries other than Australia. For each country, you can select which products are needed: Data Retrieval, Registry Documents, or UBO Verify.',
        pdfLabel: 'International KYB',
        pdfDescription: 'Whether international KYB is required.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_international_country_products',
        tabId: 'kyb',
        sectionId: 'kyb_international',
        groupId: 'kyb_international_group',
        path: 'kybConfig.internationalCountryProducts',
        valueType: 'json',
        formLabel: 'Country Products',
        formDescription: 'Per-country product selection for international KYB.',
        tooltip: 'For each country, select the products needed: Data Retrieval provides company data from registries, Registry Documents retrieves official certificates and filed records, UBO Verify identifies ultimate beneficial owners in that jurisdiction.',
        pdfLabel: 'International country products',
        pdfDescription: 'Products selected per country.',
        contributesToCompletion: false,
      },
    ],
  },
  // Trust Analyser
  {
    id: 'kyb_trust_analyser',
    tabId: 'kyb',
    formTitle: 'Trust Analyser',
    formDescription: 'Trust verification requirements and trust type percentage split.',
    pdfTitle: 'KYB - Trust Analyser',
    pdfDescription: 'Trust verification and trust type split.',
    highLevelSummary: 'Trust Analyser enablement and trust type percentages.',
    groups: [
      {
        id: 'kyb_trust_analyser_group',
        tabId: 'kyb',
        sectionId: 'kyb_trust_analyser',
        formTitle: 'Trust Analyser',
        pdfTitle: 'Trust Analyser',
        pdfDescription: 'Trust verification requirements.',
      },
    ],
    fields: [
      {
        id: 'kyb_trust_analyser_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_trust_analyser',
        groupId: 'kyb_trust_analyser_group',
        path: 'kybConfig.trustAnalyserEnabled',
        valueType: 'tri_state',
        formLabel: 'Enable Trust Analyser',
        formDescription: 'Whether Trust Analyser verification is required for trust entities.',
        tooltip: 'Trust Analyser verifies trust structures by analyzing trust deeds, identifying trustees, beneficiaries, and appointors. The three supported types are Unit Trusts, Discretionary Trusts, and SMSFs — everything else falls under Other.',
        pdfLabel: 'Trust Analyser enabled',
        pdfDescription: 'Whether Trust Analyser is enabled.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_trust_split_unit',
        tabId: 'kyb',
        sectionId: 'kyb_trust_analyser',
        groupId: 'kyb_trust_analyser_group',
        path: 'kybConfig.trustTypeSplit.unitPercent',
        valueType: 'number',
        formLabel: 'Unit Trusts %',
        formDescription: 'Percentage of unit trust entities.',
        pdfLabel: 'Unit Trusts %',
        pdfDescription: 'Percentage of unit trust entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_trust_split_discretionary',
        tabId: 'kyb',
        sectionId: 'kyb_trust_analyser',
        groupId: 'kyb_trust_analyser_group',
        path: 'kybConfig.trustTypeSplit.discretionaryPercent',
        valueType: 'number',
        formLabel: 'Discretionary Trusts %',
        formDescription: 'Percentage of discretionary trust entities.',
        pdfLabel: 'Discretionary Trusts %',
        pdfDescription: 'Percentage of discretionary trust entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_trust_split_smsf',
        tabId: 'kyb',
        sectionId: 'kyb_trust_analyser',
        groupId: 'kyb_trust_analyser_group',
        path: 'kybConfig.trustTypeSplit.smsfPercent',
        valueType: 'number',
        formLabel: 'SMSF %',
        formDescription: 'Percentage of SMSF entities.',
        pdfLabel: 'SMSF %',
        pdfDescription: 'Percentage of SMSF entities.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_trust_split_other',
        tabId: 'kyb',
        sectionId: 'kyb_trust_analyser',
        groupId: 'kyb_trust_analyser_group',
        path: 'kybConfig.trustTypeSplit.otherPercent',
        valueType: 'number',
        formLabel: 'Other Trust Types %',
        formDescription: 'Percentage of other trust type entities.',
        pdfLabel: 'Other Trust Types %',
        pdfDescription: 'Percentage of other trust type entities.',
        contributesToCompletion: false,
      },
    ],
  },
  // KYB Onboarding Form
  {
    id: 'kyb_onboarding_form',
    tabId: 'kyb',
    formTitle: 'KYB Onboarding Form',
    formDescription: 'KYB onboarding form configuration and branding.',
    pdfTitle: 'KYB - Onboarding Form',
    pdfDescription: 'KYB onboarding form settings.',
    highLevelSummary: 'KYB onboarding form enablement and branding.',
    groups: [
      {
        id: 'kyb_onboarding_form_group',
        tabId: 'kyb',
        sectionId: 'kyb_onboarding_form',
        formTitle: 'KYB Onboarding Form',
        pdfTitle: 'KYB Onboarding Form',
        pdfDescription: 'KYB onboarding form settings.',
      },
    ],
    fields: [
      {
        id: 'kyb_onboarding_form_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_onboarding_form',
        groupId: 'kyb_onboarding_form_group',
        path: 'kybConfig.onboardingFormEnabled',
        valueType: 'tri_state',
        formLabel: 'Enable KYB Onboarding Form',
        formDescription: 'Whether the customer will use FrankieOne\'s hosted KYB onboarding form.',
        tooltip: 'FrankieOne\'s KYB onboarding form provides a white-label, hosted interface for collecting business information, documents, and stakeholder details.',
        pdfLabel: 'KYB Onboarding Form enabled',
        pdfDescription: 'Whether KYB onboarding form is enabled.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_onboarding_form_branded',
        tabId: 'kyb',
        sectionId: 'kyb_onboarding_form',
        groupId: 'kyb_onboarding_form_group',
        path: 'kybConfig.onboardingFormBranded',
        valueType: 'tri_state',
        formLabel: 'Branded KYB Form',
        formDescription: 'Whether the onboarding form will be branded with customer logo and colors.',
        tooltip: 'Branding customizes the KYB onboarding form with your logo, color scheme, fonts, and styling to maintain brand consistency.',
        pdfLabel: 'Branded KYB form',
        pdfDescription: 'Whether KYB form is branded.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'kybConfig.onboardingFormEnabled', equals: 'yes' }],
      },
    ],
  },
  // Continuous KYB Monitoring
  {
    id: 'kyb_continuous_monitoring',
    tabId: 'kyb',
    formTitle: 'Continuous KYB Monitoring',
    formDescription: 'Ongoing monitoring of business entities after initial onboarding via CreditorWatch alerts.',
    pdfTitle: 'KYB - Continuous Monitoring',
    pdfDescription: 'Continuous KYB monitoring configuration.',
    highLevelSummary: 'Continuous KYB monitoring enablement (CreditorWatch).',
    groups: [
      {
        id: 'kyb_continuous_monitoring_group',
        tabId: 'kyb',
        sectionId: 'kyb_continuous_monitoring',
        formTitle: 'Continuous KYB Monitoring',
        pdfTitle: 'Continuous KYB Monitoring',
        pdfDescription: 'Continuous KYB monitoring configuration.',
      },
    ],
    fields: [
      {
        id: 'kyb_continuous_monitoring_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_continuous_monitoring',
        groupId: 'kyb_continuous_monitoring_group',
        path: 'kybConfig.continuousKybEnabled',
        valueType: 'tri_state',
        formLabel: 'Enable Continuous KYB Monitoring',
        formDescription: 'Whether continuous KYB monitoring is required for this customer.',
        tooltip: 'Continuous KYB subscribes entities to CreditorWatch alerts. FrankieOne polls daily and delivers notifications (webhook/email/dashboard) when changes occur — ownership, ASIC/ABR status, insolvency/credit events, RiskScore changes, etc.',
        pdfLabel: 'Continuous KYB enabled',
        pdfDescription: 'Whether continuous KYB monitoring is enabled.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_continuous_monitoring_notes',
        tabId: 'kyb',
        sectionId: 'kyb_continuous_monitoring',
        groupId: 'kyb_continuous_monitoring_group',
        path: 'kybConfig.continuousKybNotes',
        valueType: 'textarea',
        formLabel: 'Continuous KYB Notes',
        formDescription: 'Additional requirements or notes about continuous KYB monitoring.',
        placeholder: 'e.g., Specific alert types needed, notification preferences, volume estimates...',
        pdfLabel: 'Continuous KYB notes',
        pdfDescription: 'Additional continuous KYB monitoring notes.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'kybConfig.continuousKybEnabled', equals: 'yes' }],
      },
    ],
  },
  // KYB Aging
  {
    id: 'kyb_aging',
    tabId: 'kyb',
    formTitle: 'KYB Data Aging',
    formDescription: 'KYB verification result aging configuration.',
    pdfTitle: 'KYB - Data Aging',
    pdfDescription: 'KYB aging configuration.',
    highLevelSummary: 'KYB data aging settings.',
    groups: [
      {
        id: 'kyb_aging_group',
        tabId: 'kyb',
        sectionId: 'kyb_aging',
        formTitle: 'KYB Data Aging',
        pdfTitle: 'KYB Data Aging',
        pdfDescription: 'KYB aging configuration.',
      },
    ],
    fields: [
      {
        id: 'kyb_aging_enabled',
        tabId: 'kyb',
        sectionId: 'kyb_aging',
        groupId: 'kyb_aging_group',
        path: 'kybConfig.agingEnabled',
        valueType: 'boolean',
        formLabel: 'Use Custom Aging Period',
        formDescription: 'Enable custom aging configuration for KYB results.',
        tooltip: 'KYB aging determines how long business verification results remain valid before requiring re-verification. Default is 12 months.',
        pdfLabel: 'Custom aging enabled',
        pdfDescription: 'Whether custom KYB aging is enabled.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_aging_period_months',
        tabId: 'kyb',
        sectionId: 'kyb_aging',
        groupId: 'kyb_aging_group',
        path: 'kybConfig.agingPeriodMonths',
        valueType: 'number',
        formLabel: 'Aging Period (Months)',
        formDescription: 'Number of months before KYB re-verification is required.',
        tooltip: 'KYB results older than this period will require re-verification when a check workflow starts.',
        pdfLabel: 'Aging period',
        pdfDescription: 'KYB aging period in months.',
        contributesToCompletion: false,
      },
      {
        id: 'kyb_aging_custom_config',
        tabId: 'kyb',
        sectionId: 'kyb_aging',
        groupId: 'kyb_aging_group',
        path: 'kybConfig.agingCustomConfig',
        valueType: 'textarea',
        formLabel: 'Additional Aging Configuration',
        formDescription: 'Custom aging rules or requirements for different business check types.',
        placeholder: 'Enter any custom aging rules or specific requirements for different business check types',
        tooltip: 'Specify different aging periods for specific business check types or special requirements.',
        pdfLabel: 'Custom aging config',
        pdfDescription: 'Additional KYB aging configuration.',
        contributesToCompletion: false,
      },
    ],
  },
]
